import { useEffect, useState, useRef } from "react";
//VC
import {
  images as imageQuerysVC,
  meetings as meetingQuerysVC,
  invites as inviteQuerysVC,
  users as usersQuerysVC,
  recs as recordingQuerysVC,
  transcription as transcriptQuerysVC,
} from "../utils/api/vchat/hooks";
import {
  scheduleMeeting,
  gradeConversation,
  getCreatorMeetings,
  getClassroomDiscussions as getClassroomDiscussionsVC,
  getTranscriptUrl,
  updateMeeting,
  deleteMeeting,
} from "../utils/api/vchat/api/Meetings";
import {
  createInvite as createInviteVC,
  getUserInvites as getUserInvitesVC,
  deleteInvite,
} from "../utils/api/vchat/api/Invites";
import {
  getUserRecs as getUserRecsVC,
  getMeetingRecs,
  getRecEvents,
  getSubmissionsRecs,
  getRec,
  updateRec,
} from "../utils/api/vchat/api/Recs";
import {
  createImage as createImageVC,
  getImage,
  getImages,
} from "../utils/api/vchat/api/Images";
import { updateUser as updateUserVC } from "../utils/api/vchat/api/Users";
import { v4 as uuidv4 } from "uuid";
import { createTranscription } from "../utils/api/vchat/api/Transcription";
import {
  TextCensor,
  RegExpMatcher,
  englishDataset,
  englishRecommendedTransformers,
} from "obscenity";
import { Profanity } from "@2toad/profanity";
import logger from "../logger";

const useVideoChat = ({
  startLoading,
  stopLoading,
  username,
  openSnackbar,
  preferred_username,
  picture,
  isVideoChatEnabled,
  isVideoChatEnabledInSettings,
  classroomId,
  t,
  classroomMembersFormatted,
  isCreator,
  learnLang,
  // Discuss AI feedback, already combined with the all-AI switch (see
  // isClassroomDiscussAIFeedbackEnabled). Grading is refused here as well as
  // hidden in the UI - the button is not the only way into this function.
  isDiscussAIFeedbackEnabled = true,
}) => {
  const [s3Url, setS3Url] = useState("");
  const [recordingFeedback, setRecordingFeedback] = useState({});
  const [createMeetingDialog, setCreateMeetingDialog] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [startTimes, setStartTimes] = useState([]);
  const [endTimes, setEndTimes] = useState([]);
  const [s3EventsUrl, setS3EventsUrl] = useState("");
  const [transcriptUrl, setTranscriptUrl] = useState("");
  const [selectedRecording, setSelectedRecording] = useState({});
  const [conversation, setConversation] = useState("");
  const [aiGrade, setAiGrade] = useState("");
  const [editMeetingImages, setEditMeetingImages] = useState([]);
  const [editBackgroundImages, setEditBackgroundImages] = useState([]);
  const [selectedDiscussion, setSelectedDiscussion] = useState({});
  const playerRef = useRef(null);
  const createImageMutation = imageQuerysVC.useCreateImage(createImageVC);
  const createInviteMutation = inviteQuerysVC.useCreateInvite(createInviteVC);
  const updateUserMutation = usersQuerysVC.useUpdateUser(updateUserVC, {
    onSettled: () => {
      stopLoading();
    },
    onSuccess: async (variables, context) => {
      await handleScheduleMeeting({ values: context.values });
      getCreatorMeetingsQuery.refetch();
    },
    onError: () => {
      openSnackbar("max_video_time_exceeded", "error");
    },
  });
  const updateRecMutation = recordingQuerysVC.useUpdateRec(updateRec);
  const updateMeetingMutation = meetingQuerysVC.useUpdateMeeting(updateMeeting);
  const deleteMeetingMutation = meetingQuerysVC.useDeleteMeeting(deleteMeeting);
  const deleteInviteMutation = inviteQuerysVC.useDeleteInvite(deleteInvite);

  const createTranscriptMutation =
    transcriptQuerysVC.useCreateTranscript(createTranscription);

  const getInvitesQuery = inviteQuerysVC.useInvites(
    async (filters) => {
      const response = await getUserInvitesVC(filters);
      return response;
    },
    {
      filters: {
        username,
        classroomId,
      }
    },
    {
      enabled: !!username && isVideoChatEnabled && isVideoChatEnabledInSettings,
    },
  );

  const inviteData =
    getInvitesQuery.isSuccess && getInvitesQuery.data
      ? getInvitesQuery.data
      : {};

  const scheduledMeetings =
    inviteData && inviteData.items ? inviteData.items : [];

  const handleJoinMeeting = async (values) => {
    const updateValues = values;
    updateValues.preferredUsername = preferred_username || "";
    updateValues.picture = picture || "";
    localStorage.setItem("joinMeetingParams", JSON.stringify(updateValues));
    window.open(`/discuss`, "_blank");
  };

  const inviteUsers = async ({ meetingMembers, meetingDetails, meetingId }) => {
    try {
      startLoading();
      await Promise.all(
        meetingMembers.map(async (id) => {
          const updateBody = {
            ...meetingDetails,
            meetingId,
            meetingMembers,
            classroom: classroomId,
          };
          await createInviteMutation.mutateAsync({ userId: id, ...updateBody });
        }),
      );
      stopLoading();
    } catch (err) {
      stopLoading();
      throw err;
    }
  };

  const handleCreateImage = async (values) => {
    try {
      startLoading();
      let newUrl = "";
      await createImageMutation.mutateAsync(
        { ...values, classroomId },
        {
          onSettled: (data) => {
            stopLoading();
            const { Item } = data;
            const { url } = Item;
            newUrl = url;
            return data;
          },
          onSuccess: (data) => {
            const { Item } = data;
            const { url } = Item;
            setS3Url(url);
            newUrl = url;
            return data;
          },
        },
      );
      return newUrl;
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteInvite = async (values) => {
    startLoading();
    await deleteInviteMutation.mutate(
      { ...values },
      {
        onSettled: () => {
          stopLoading();
        },
        onSuccess: () => { },
        onError: (error) => {
          throw error;
        },
      },
    );
  };

  const handleDeleteMeeting = async (values) => {
    startLoading();
    await deleteMeetingMutation.mutate(
      { ...values },
      {
        onSettled: () => {
          stopLoading();
        },
        onSuccess: () => { },
        onError: (error) => {
          throw error;
        },
      },
    );
  };

  const handleScheduleMeeting = async ({ values }) => {
    try {
      const { groups = [] } = values;
      const { backgroundImages: newBGimages, meetingImages: newMimgaes } =
        values;
      const groupedId = values.groupedId || uuidv4();
      await Promise.all(
        groups.map(async (memberGroup) => {
          delete values.members;
          const backgroundImages =
            (newBGimages?.length > 0 && newBGimages) || [];
          const meetingImages = (newMimgaes?.length > 0 && newMimgaes) || [];
          const mappedBIGs = backgroundImages.map(
            (imgData) => imgData.src || imgData,
          );
          const mappedMIGs = meetingImages.map(
            (imgData) => imgData.src || imgData,
          );
          setEditMeetingImages(mappedMIGs);
          setEditBackgroundImages(mappedBIGs);
          const formattedValues = values;
          delete formattedValues.groups;
          delete formattedValues.backgroundImages;
          delete formattedValues.editMeetingImages;
          delete formattedValues.editBackgroundImages;
          delete formattedValues.meetingImages;
          delete formattedValues.meetingId;
          formattedValues.creator = username;
          formattedValues.groupSize = memberGroup?.length;
          const response = await scheduleMeeting({
            ...formattedValues,
            meetingMembers: memberGroup,
            meetingRecorded: "false",
            classroom: classroomId,
            classroomId: classroomId,
            groupedId,
          });
          const { item } = response;
          if (backgroundImages?.length) {
            await Promise.all(
              backgroundImages.map(async (imgData) => {
                await handleCreateImage({
                  userId: username,
                  meetingId: item.meetingId,
                  image: imgData.src || imgData,
                  fileExt: imgData.ext || "jpeg",
                  imageType: "background",
                });
              }),
            );
            formattedValues.url = s3Url;
          }
          if (meetingImages?.length) {
            await Promise.all(
              meetingImages.map(async (imgData) => {
                await handleCreateImage({
                  userId: username,
                  meetingId: item.meetingId,
                  image: imgData.src || imgData,
                  fileExt: imgData.ext || "jpeg",
                  imageType: "meeting",
                });
              }),
            );
            formattedValues.url = s3Url;
          }
          await inviteUsers({
            meetingMembers: [memberGroup].flat(),
            meetingDetails: item,
            meetingId: item.meetingId,
            meetingRecorded: "false",
          });
        }),
      );
      setCreateMeetingDialog(false);
      setSelectedDiscussion({});
      getInvitesQuery.refetch();
      getCreatorMeetingsQuery.refetch();
      if (isCreator && isVideoChatEnabled && isVideoChatEnabledInSettings) {
        getClassroomDiscussionsQuery.refetch();
      }
      stopLoading();
      openSnackbar("discussion_created", "success");
    } catch (err) {
      openSnackbar("schedule_discussion_error", "error");
    }
  };

  const handleUpdateUser = async ({ values, timeRequested, userId }) => {
    try {
      startLoading();
      await updateUserMutation.mutate({ timeRequested, userId, values });
      stopLoading();
    } catch (err) {
      openSnackbar(t("update_discussion_error"), "error");
      stopLoading();
    }
  };

  const syncInvitesForMeeting = async ({
    meetingId,
    meetingDetails,
    desiredMembers,
    currentMembers,
  }) => {
    const cleanedDesired = Array.from(
      new Set((desiredMembers || []).filter(Boolean)),
    );

    // If no desired members, do nothing (prevents removing invites by accident)
    if (cleanedDesired.length === 0) {
      return;
    }

    const cleanedCurrent = Array.from(
      new Set((currentMembers || []).filter(Boolean)),
    );

    const desiredSet = new Set(cleanedDesired);
    const currentSet = new Set(cleanedCurrent);

    const toRemove = cleanedCurrent.filter((id) => !desiredSet.has(id));
    const toAdd = cleanedDesired.filter((id) => !currentSet.has(id));
    const toKeep = cleanedDesired.filter((id) => currentSet.has(id));

    await Promise.all(
      toRemove.map((userId) => handleDeleteInvite({ meetingId, userId })),
    );

    await Promise.all(
      [...toAdd, ...toKeep].map((userId) =>
        createInviteMutation.mutateAsync({
          userId,
          ...meetingDetails,
          meetingId,
          meetingMembers: cleanedDesired,
          classroom: classroomId,
        }),
      ),
    );
  };

  // Mutations
  const handleUpdateDiscussion = async ({ creator, meetingId, values }) => {
    try {
      startLoading();
      await updateMeetingMutation.mutateAsync(
        { creator, meetingId, classroomId, values },
        {
          onSettled: () => {
            stopLoading();
          },
          onSuccess: async () => {
            openSnackbar(t("archive_discussions"), "success");
            getCreatorMeetingsQuery.refetch();
          },
        },
      );
    } catch (error) {
      openSnackbar(t("update_discussion_error"), "error");
      stopLoading();
      throw error;
    }
  };

  const handleArchiveDiscussions = async (discussions) => {
    try {
      await Promise.all(
        discussions.map(async (discussion) => {
          const { meetingMembers, meetingId, creator } = discussion;
          await Promise.all(
            meetingMembers.map(async (userId) => {
              await handleDeleteInvite({ meetingId, userId });
            }),
          );
          await handleUpdateDiscussion({
            creator,
            meetingId,
            values: { archived: true },
          });
        }),
      );
    } catch (error) {
      stopLoading();
      throw error;
    }
  };

  const handleUpdateRecording = async (values) => {
    try {
      startLoading();
      await updateRecMutation.mutateAsync(
        { ...values },
        {
          onSettled: () => {
            stopLoading();
          },
          onSuccess: async () => {
            openSnackbar(t("archive_submissions"), "success");
            getSubmissionsQuery.refetch();
          },
        },
      );
      stopLoading();
    } catch (error) {
      openSnackbar(t("update_recording_error"), "error");
      stopLoading();
    }
  };

  const handleArchiveRecording = async (recordings) => {
    try {
      await Promise.all(
        recordings.map(async (recording) => {
          await handleUpdateRecording({
            userId: recording.userId,
            creator: recording.creator,
            meetingId: recording.meetingId,
            values: { archived: true },
            extMeetingId: recording.extMeetingId,
          });
        }),
      );
    } catch (error) {
      openSnackbar(t("archive_recording_error"), "error");
      stopLoading();
    }
  };

  const normalizeGroups = (groups = []) =>
    (groups || [])
      .map((g) => Array.from(new Set((g || []).flat().filter(Boolean))))
      .filter((g) => g.length > 0);

  const buildMeetingPatch = ({ newValues, meetingMembers }) => ({
    groupSize: meetingMembers?.length || 0,
    joinInfo: newValues.joinInfo,
    meetingDate: newValues.meetingDate,
    meetingMembers,
    groupedId: newValues.groupedId,
    meetingPrompt: newValues.meetingPrompt,
    meetingTime: newValues.meetingTime,
    meetingTopic: newValues.meetingTopic,
    recordTimeLimit: newValues.recordTimeLimit,
    timeToLive: newValues.timeToLive,
  });

  const handleUpdateDiscussions = async ({ newValues, discussions }) => {
    try {
      let groupedId;
      startLoading();
      const newGroups = normalizeGroups(newValues?.groups || []);
      const existingDiscussions = Array.isArray(discussions) ? discussions : [];

      const groupCount = newGroups.length;
      const discussionCount = existingDiscussions.length;

      const commonCount = Math.min(groupCount, discussionCount);
      // Update the overlapping range (0..commonCount-1)
      await Promise.all(
        existingDiscussions
          .slice(0, commonCount)
          .map(async (discussion, index) => {
            const desiredMembers = newGroups[index];
            const meetingId = discussion.meetingId;
            groupedId = discussion.groupedId;
            // If this ever happens due to normalization, archive to avoid empty meetings
            if (!desiredMembers || desiredMembers.length === 0) {
              await updateMeetingMutation.mutateAsync({
                creator: discussion.creator,
                meetingId,
                classroomId,
                values: { archived: true },
              });
              return;
            }

            const meetingPatch = buildMeetingPatch({
              newValues,
              meetingId,
              meetingMembers: desiredMembers,
            });

            // Update meeting in place
            await updateMeetingMutation.mutateAsync({
              creator: discussion.creator,
              meetingId,
              classroomId,
              values: meetingPatch,
            });

            // Sync invites in place
            await syncInvitesForMeeting({
              meetingId,
              meetingDetails: { ...discussion, ...meetingPatch, meetingId },
              desiredMembers,
              currentMembers: discussion.meetingMembers || [],
            });
          }),
      );

      if (groupCount > discussionCount) {
        const extraGroups = newGroups.slice(discussionCount);
        const scheduleValues = { ...newValues, groups: extraGroups, groupedId };
        await handleScheduleMeeting({ values: scheduleValues });
      }

      if (discussionCount > groupCount) {
        const extras = existingDiscussions.slice(groupCount);
        await Promise.all(
          extras.map(async (discussion) => {
            try {
              console.log(
                "Deleting meeting",
                discussion.meetingId,
                "creator",
                discussion.creator,
              );

              await handleDeleteMeeting({
                meetingId: discussion.meetingId,
                creator: discussion.creator,
                classroomId: classroomId,
              });

              await Promise.all(
                (discussion.meetingMembers || []).map((memberId) =>
                  handleDeleteInvite({
                    meetingId: discussion.meetingId,
                    userId: memberId,
                  }),
                ),
              );
            } catch (e) {
              console.error(
                "Failed extra cleanup for meeting",
                discussion.meetingId,
                e,
              );
              throw e;
            }
          }),
        );
      }

      openSnackbar(t("discussion_updated"), "success");
      getInvitesQuery.refetch();
      getCreatorMeetingsQuery.refetch();
    } catch (error) {
      openSnackbar(t("update_discussion_error"), "error");
      throw error;
    } finally {
      stopLoading();
    }
  };

  const formattedScheduleMeeting = async (values) => {
    try {
      startLoading();
      let totalNumberStudents = 0;
      const { groups = [] } = values;
      groups.forEach((group) => {
        totalNumberStudents += group?.length;
      });
      const timeRequested = totalNumberStudents * 10;
      await handleUpdateUser({ userId: classroomId, timeRequested, values });
      stopLoading();
    } catch (err) {
      openSnackbar(t("update_discussion_error"), "error");
      stopLoading();
    }
  };

  function dateDifferenceInSeconds(start, stop) {
    const date1 = new Date(start);
    const date2 = new Date(stop);
    // Calculate the time difference in milliseconds
    const timeDifferenceInMilliseconds = date2 - date1;
    // Convert the time difference to seconds
    const differenceInSeconds = timeDifferenceInMilliseconds / 1000;
    return differenceInSeconds;
  }

  const getRecordingsQuery = recordingQuerysVC.useRecordings(
    async () => {
      try {
        const resp = await getUserRecsVC({
          creator: username,
          classroomId,
        });
        return resp?.items || {};
      } catch (err) {
        openSnackbar(t("get_recs_error"), "error");
      }
    },
    {
      classroomId,
      username,
    },
    {
      enabled: isVideoChatEnabled && isVideoChatEnabledInSettings,
    },
  );
  const recordingData =
    getRecordingsQuery.isSuccess && getRecordingsQuery.data
      ? getRecordingsQuery.data
      : [];
  const gradeConversationMutation =
    meetingQuerysVC.useGradeConversation(gradeConversation);

  const fetchConversationForRecording = async ({
    meetingCreator,
    meetingId,
    extMeetingId,
    creator,
  }) => {
    try {
      const transcriptUrlData = await getTranscriptUrl({
        creator,
        meetingId,
        extMeetingId,
        meetingCreator,
        classroomId,
      });
      const signedTranscriptUrl = transcriptUrlData.item.transcript;

      if (!signedTranscriptUrl) {
        throw new Error("Transcript URL not found");
      }

      if (signedTranscriptUrl) {
        const transcriptsResponse = await fetch(signedTranscriptUrl);
        const jsonres = await transcriptsResponse.json();

        const censor = new TextCensor();
        const matcher = new RegExpMatcher({
          ...englishDataset.build(),
          ...englishRecommendedTransformers,
        });

        const filteredConversation = jsonres.map((item) => {
          if (item.text && typeof item.text === "string") {
            const matches = matcher.getAllMatches(item.text);
            return {
              ...item,
              text: censor.applyTo(item.text, matches),
            };
          }
          return item;
        });

        const formattedConversation = filteredConversation.map((item) => {
          const correctMember = classroomMembersFormatted.find(
            (member) => member.memberId === item.userId,
          );
          return {
            ...item,
            user: correctMember?.username || item.userId,
          };
        });

        return formattedConversation;
      }
    } catch (error) {
      logger.error("Error fetching conversation:", error);
      throw error;
    }
  };

  const handleBatchGradeConversations = async (conversationsArray) => {
    if (!isDiscussAIFeedbackEnabled) {
      openSnackbar(t("ai_limited_feedback_blocked"), "info");
      return;
    }
    try {
      // startLoading();
      const inferredGradeMode =
        conversationsArray.length === 1 ? "individual" : "batch";
      const results = await Promise.all(
        conversationsArray.map(async (values) => {
          let conversationToGrade = values.conversation;

          if (!conversationToGrade || conversationToGrade.length === 0) {
            conversationToGrade = await fetchConversationForRecording({
              meetingCreator: values.meetingCreator || username,
              meetingId: values.meetingId,
              extMeetingId: values.extMeetingId,
              creator: values.creator,
            });
          }

          const data = await gradeConversationMutation.mutateAsync({
            id: username,
            ...values,
            gradeMode: inferredGradeMode,
            conversation: conversationToGrade,
          });

          return {
            meetingId: values.meetingId,
            grade: data.item,
          };
        }),
      );

      const newGrades = {};
      results.forEach(({ meetingId, grade }) => {
        newGrades[meetingId] = grade;
      });

      setAiGrade((prev) => ({
        ...prev,
        ...newGrades,
      }));

      openSnackbar(t("conversations_graded_success"), "success");
      // stopLoading();
    } catch (error) {
      openSnackbar("grade_conversation_error", "error");
      // stopLoading();
    }
  };

  const imageUrlToBase64 = async (url) => {
    try {
      startLoading();
      const data = await fetch(url).catch((e) => logger.error(e));
      const blob = await data.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          const base64data = reader.result;
          stopLoading();
          resolve(base64data);
        };
        reader.onerror = (e) => {
          logger.error({ e });
          reject(e);
        };
      });
    } catch (err) {
      stopLoading();
      logger.error({ err });
    }
  };

  const getImagesQuery = imageQuerysVC.useImages(
    async () => {
      try {
        const data = selectedDiscussion;
        const { meetingId, creator } = data;
        const images = await getImages({
          id: `${meetingId}|${creator}`,
          classroomId,
        });
        const backgroundImagesData = [];
        const meetingImagesData = [];
        images.item.forEach((image) => {
          if (image.imageType === "background") {
            backgroundImagesData.push(image);
          } else if (image.imageType === "meeting") {
            meetingImagesData.push(image);
          }
        });
        const signedBackgroundImageB64 = [];
        const signedMeetingImageB64 = [];
        await Promise.all(
          backgroundImagesData.map(async (imageData) => {
            const { created, id, imageType } = imageData;
            const signedImageData = await getImage({
              id,
              created,
              classroomId,
            });
            const { item } = signedImageData;
            const { url } = item;
            const b64 = await imageUrlToBase64(url);
            const newSignedImage = imageData;
            newSignedImage.url = url;
            if (imageType === "background") signedBackgroundImageB64.push(b64);
            if (imageType === "meeting") signedMeetingImageB64.push(b64);
          }),
        );
        await Promise.all(
          meetingImagesData.map(async (imageData) => {
            const { created, id, imageType } = imageData;
            const signedImageData = await getImage({
              id,
              created,
              classroomId,
            });
            const { item } = signedImageData;
            const { url } = item;
            const b64 = await imageUrlToBase64(url);
            if (imageType === "background") signedBackgroundImageB64.push(b64);
            if (imageType === "meeting") signedMeetingImageB64.push(b64);
          }),
        );
        setEditBackgroundImages(signedBackgroundImageB64);
        setImageLoading(false);
        setEditMeetingImages(signedMeetingImageB64);
        stopLoading();
      } catch (err) {
        openSnackbar(t("get_image_error"), "error");
        stopLoading();
      }
    },
    {
      selectedDiscussion,
    },
    {
      enabled: !!(
        selectedDiscussion &&
        selectedDiscussion?.meetingId &&
        selectedDiscussion?.creator
      ),
    },
  );

  useEffect(() => {
    setEditBackgroundImages([]);
    setEditMeetingImages([]);
    if (
      !!(
        selectedDiscussion &&
        selectedDiscussion?.meetingId &&
        selectedDiscussion?.creator
      )
    ) {
      getImagesQuery.refetch();
    }
  }, [selectedDiscussion]);

  // Mutations
  const getCreatorMeetingsQuery = meetingQuerysVC.useCreatorMeetings(
    async () => {
      try {
        const creatorMeetingResponse = await getCreatorMeetings({
          creator: username,
          classroom: classroomId,
        });
        return creatorMeetingResponse?.items || [];
      } catch (error) {
        return [];
      }
    },
    {
      username,
      filters: {
        creator: username,
        classroom: classroomId,
      },
    },
    {
      enabled: !!(
        username &&
        isVideoChatEnabled &&
        isVideoChatEnabledInSettings
      ),
    },
  );

  const getClassroomDiscussionsQuery = meetingQuerysVC.useClassroomDiscussions(
    async () => {
      try {
        const classroomDiscussionsResponse = await getClassroomDiscussionsVC({
          classroom: classroomId,
        });
        return classroomDiscussionsResponse?.items || [];
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    {
      filters: {
        classroom: classroomId,
      }
    },
    {
      enabled: !!(
        isCreator &&
        isVideoChatEnabled &&
        isVideoChatEnabledInSettings
      ),
    },
  );

  const classroomDiscussions =
    getClassroomDiscussionsQuery.isSuccess &&
      getClassroomDiscussionsQuery.data
      ? getClassroomDiscussionsQuery.data
      : [];


  // Mutations
  const saveTranscript = async (conversation) => {
    startLoading();
    createTranscriptMutation.mutate(
      { conversation, aiGrade, recordingFeedback },
      {
        onSettled: () => {
          stopLoading();
        },
        onSuccess: async () => { },
        onError: (error) => {
          logger.error(`Mutation failed: ${error}`);
          openSnackbar("problem", "error");
        },
      },
    );
  };

  recordingQuerysVC.useRecEvents(
    async () => {
      try {
        startLoading();
        const { meetingId, extMeetingId, creator, meetingCreator } =
          selectedRecording;
        const recEvents = await getRecEvents({
          recCreator: creator,
          meetingCreator,
          meetingId,
          prefix: "composited-video",
          extMeetingId,
        });
        const contents = recEvents?.items?.s3Events?.Contents ?? [];
        if (contents.length === 0) {
          setS3Url("");
          stopLoading();
          return {};
        }
        const sorted = [...contents].sort(
          (a, b) => new Date(b.LastModified) - new Date(a.LastModified),
        );
        const s3Path = sorted[0].Key;
        const signedRecUrlData = await getRec({
          s3Path,
          extMeetingId,
          meetingCreator,
        });
        const signedRecUrl = signedRecUrlData?.item?.transcript;
        if (!signedRecUrl) {
          throw new Error("Invalid signedRecUrlData structure");
        }

        stopLoading();
        setS3Url(signedRecUrl);
        return recEvents;
      } catch {
        openSnackbar(t("get_recs_error"), "error");
        stopLoading();
        return {};
      }
    },
    {
      userId: username,
      creator: selectedRecording.creator,
      meetingId: selectedRecording.meetingId,
      prefix: "composited-video",
    },
    {
      enabled:
        !!(selectedRecording?.meetingId && selectedRecording?.creator) &&
        isVideoChatEnabled &&
        isVideoChatEnabledInSettings,
      staleTime: 1000 * 60 * 5,
    },
  );

  const goToTimestamp = (chosenTime) => {
    if (!playerRef.current) {
      console.warn("Player ref not ready");
      return;
    }

    let numberString = chosenTime.toString();
    numberString = numberString.substring(0, 10);
    const formattedTime = parseInt(numberString, 10);
    const dateObj = new Date(formattedTime * 1000);
    const _chosenTime = dateObj.toISOString();

    let chosenTimeSecs = dateDifferenceInSeconds(startTimes[0], _chosenTime);

    endTimes.forEach((time, index) => {
      if (chosenTimeSecs > dateDifferenceInSeconds(startTimes[0], time)) {
        const timeSkipped = dateDifferenceInSeconds(
          time,
          startTimes[index + 1],
        );
        chosenTimeSecs = chosenTimeSecs - timeSkipped;
      }
    });

    const finalTime = chosenTimeSecs || 0;
    if (typeof playerRef.current.seekTo === "function") {
      playerRef.current.seekTo(finalTime, "seconds");
    } else if ("currentTime" in playerRef.current) {
      playerRef.current.currentTime = finalTime;
    } else {
      console.warn(
        "Player ref has no seekTo or currentTime",
        playerRef.current,
      );
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { meetingCreator, meetingId, extMeetingId, creator } =
          selectedRecording;
        setConversation("");
        if (meetingCreator && meetingId && extMeetingId) {
          const transcriptUrlData = await getTranscriptUrl({
            creator,
            meetingId,
            extMeetingId,
            meetingCreator,
            classroomId,
          });
          const signedTranscriptUrl = transcriptUrlData.item.transcript;
          setTranscriptUrl(signedTranscriptUrl);
        }
      } catch (error) {
        openSnackbar(t("get_transcript_error"), "error");
        stopLoading();
      }
    })();
  }, [selectedRecording]);

  const languages = ["en"];

  const languageMapping = {
    spanish: "es",
    italian: "it",
    german: "de",
    english: "en",
    french: "fr",
    portuguese: "pt",
  };

  useEffect(() => {
    if (learnLang && learnLang.trim() !== "") {
      const languageCode = languageMapping[learnLang];
      languages.push(languageCode);
    }

    const profanity = new Profanity({
      languages: languages,
    });

    async function getS3Data({ transcriptUrl, s3EventsUrl }) {
      try {
        if (transcriptUrl) {
          const transcriptsResponse = await fetch(transcriptUrl);
          const jsonres = await transcriptsResponse.json();

          const filteredConversation = jsonres.map((item) => {
            if (item.text && typeof item.text === "string") {
              if (profanity.exists(item.text)) {
              }
              return {
                ...item,
                text: profanity.censor(item.text),
              };
            }
            return item;
          });
          const formattedConversation = filteredConversation.map((item) => {
            const correctMember = classroomMembersFormatted.find(
              (member) => member.memberId === item.userId,
            );
            return {
              ...item,
              user: correctMember?.username || item.userId,
            };
          });
          setConversation(formattedConversation);
        }

        if (s3EventsUrl) {
          const videoEventsResponse = await fetch(s3EventsUrl);
          const videoEventsResponseJson = await videoEventsResponse.text();
          const regex = /{[^{}]*}(?=\n|$)/g;
          const jsonObjects = videoEventsResponseJson.match(regex) || [];
          const items = jsonObjects.map((json) => JSON.parse(json));
          const _startTimes = [];
          const _endTimes = [];
          items.forEach((event) => {
            if (event.EventType === "CaptureStarted") {
              _startTimes.push(event.Timestamp);
            }
            if (event.EventType === "CaptureEnded") {
              _endTimes.push(event.Timestamp);
            }
          });
          setStartTimes(_startTimes);
          setEndTimes(_endTimes);
        }
      } catch (err) {
        logger.error(err);
      }
    }

    const runMe = async () => {
      await getS3Data({ transcriptUrl, s3EventsUrl });
    };
    runMe();
  }, [transcriptUrl, s3EventsUrl]);

  // Mutations
  recordingQuerysVC.useRecEvents(
    async () => {
      try {
        startLoading();
        const { meetingId, extMeetingId, meetingCreator, creator } =
          selectedRecording;
        const recEvents = await getRecEvents({
          recCreator: creator,
          meetingCreator,
          meetingId,
          prefix: "meeting-events",
          extMeetingId,
        });
        const contents = recEvents?.items?.s3Events?.Contents ?? [];
        if (contents.length === 0) {
          setS3EventsUrl("");
          stopLoading();
          return {};
        }
        const s3Path = contents[0].Key;
        const signedEventsUrlData = await getRec({
          extMeetingId,
          s3Path,
          meetingCreator,
        });
        // Ensure signedEventsUrlData and its nested properties are defined
        if (!signedEventsUrlData?.item?.transcript) {
          throw new Error("Invalid signedEventsUrlData structure");
        }
        const signedEventsUrl = signedEventsUrlData.item.transcript;
        stopLoading();
        if (
          recEvents?.items &&
          recEvents.items.s3Events?.Contents &&
          signedEventsUrl
        ) {
          setS3EventsUrl(signedEventsUrl);
        }
        return recEvents;
      } catch (err) {
        openSnackbar(t("get_recs_error"), "error");
        stopLoading();
        return {}; // Ensure a value is returned in case of error
      }
    },
    {
      userId: username,
      creator: selectedRecording.creator,
      meetingId: selectedRecording.meetingId,
      prefix: "meeting-events",
    },
    {
      enabled:
        !!(selectedRecording?.meetingId && selectedRecording?.creator) &&
        isVideoChatEnabled &&
        isVideoChatEnabledInSettings,
    },
  );

  const getSubmissionsQuery = recordingQuerysVC.useSubmissions(
    async () => {
      try {
        const resp = await getSubmissionsRecs({
          meetingCreator: username,
          classroom: classroomId,
        });
        return resp?.items || [];
      } catch (err) {
        openSnackbar(t("get_subs_error"), "error");
        return []; // Ensure a default value is returned in case of error
      }
    },
    {
      meetingCreator: username,
      classroom: classroomId,
    },
    {
      enabled: !!username && isVideoChatEnabled && isVideoChatEnabledInSettings,
    },
  );
  const submissionsData =
    getSubmissionsQuery.isSuccess && getSubmissionsQuery.data
      ? getSubmissionsQuery.data
      : [];

  const getMeetingRecsQuery = recordingQuerysVC.useMeetingRecs(
    async () => {
      try {
        startLoading();
        const { meetingId, meetingCreator } = selectedRecording || {};

        if (!meetingId || !meetingCreator) {
          stopLoading();
          return [];
        }

        const getMeetingRecsResponse = await getMeetingRecs({
          meetingId,
          meetingCreator,
        });
        stopLoading();
        return getMeetingRecsResponse.items || [];
      } catch (err) {
        openSnackbar(t("get_recs_error"), "error");
        stopLoading();
        return [];
      }
    },
    {
      meetingId: selectedRecording?.meetingId,
      meetingCreator: selectedRecording?.meetingCreator,
      filters: {
        meetingId: selectedRecording?.meetingId,
      },
    },
    {
      enabled:
        username &&
        isVideoChatEnabled &&
        isVideoChatEnabledInSettings &&
        selectedRecording?.meetingCreator === username,
    },
  );

  const meetingRecsData =
    getMeetingRecsQuery.isSuccess && getMeetingRecsQuery.data
      ? getMeetingRecsQuery.data
      : null;

  const handleCreateFeedback = async (values, recordingData) => {
    try {
      startLoading();
      const recording = recordingData || selectedRecording;
      const { meetingId, creator, extMeetingId } = recording;
      if (!meetingId || !creator || !extMeetingId) {
        console.error("Missing recording data:", {
          meetingId,
          creator,
          extMeetingId,
        });
        throw new Error("Missing required recording information");
      }

      const { selectedAttendee } = values || [];
      await Promise.all(
        [selectedAttendee].flat().map(async (id) => {
          const newValues = { ...values, selectedAttendee: id };
          await updateRecMutation.mutateAsync(
            {
              userId: id,
              creator,
              meetingId,
              extMeetingId,
              values: { ...newValues },
            },
            {
              onSettled: () => {
                stopLoading();
              },
              onSuccess: async () => {
                openSnackbar(t("feedback_submitted"), "success");
                if (selectedRecording?.meetingId === meetingId) {
                  getMeetingRecsQuery.refetch();
                }
              },
            },
          );
        }),
      );
    } catch (error) {
      openSnackbar(t("create_feedback_error"), "error");
      stopLoading();
    }
  };

  const resetVideoState = () => {
    setSelectedRecording({
      meetingId: undefined,
      creator: undefined,
    });
    setS3Url("");
    setTranscriptUrl("");
    setConversation("");
  };

  return {
    scheduledMeetings,
    selectedRecording,
    setSelectedRecording,
    handleBatchGradeConversations,
    aiGrade,
    imageLoading,
    goToTimestamp,
    conversation,
    s3EventsUrl,
    getSubmissionsQuery,
    submissionsData,
    meetingRecsData,
    getInvitesQuery,
    s3Url,
    resetVideoState,
    setS3Url,
    setTranscriptUrl,
    playerRef,
    formattedScheduleMeeting,
    handleJoinMeeting,
    createMeetingDialog,
    setCreateMeetingDialog,
    handleCreateFeedback,
    createdDiscussions: classroomDiscussions,
    refreshCreatedDiscussions: isCreator
      ? getClassroomDiscussionsQuery.refetch
      : undefined,
    recordingData,
    refreshRecordings: getRecordingsQuery.refetch,
    recordingFeedback,
    saveTranscript,
    handleArchiveDiscussions,
    handleArchiveRecording,
    handleUpdateDiscussions,
    setSelectedDiscussion,
    editBackgroundImages,
    editMeetingImages,
    fetchConversationForRecording,
    getCreatorMeetingsQuery,
    startTimes,
  };
};
export default useVideoChat;
